import React, { useCallback, useEffect, useState } from "react";
import { getOutreachSnapshot, approveOutreachSnapshot } from "../../api/analyticsApi";
import "./ProposedActionsCard.css";
import { money } from "../Revival/revivalFormat";

const activeTenantName = () => {
  try {
    return JSON.parse(localStorage.getItem("activeTenant") || "null")?.name || "this workspace";
  } catch {
    return "this workspace";
  }
};

/* Outreach engine review surface — renders FROM the CampaignReviewSnapshot
   (review-as-document): classification counts, ranked thread-aware drafts
   with quoted evidence, the hold queue, the exclusion ledger with thread
   quotes, and said-yes follow-through. "Stage for send" mints the
   ProposedAction; the final Approve & send stays on the AI Proposals card. */

const KIND_LABEL = { text: "their text", email: "their email", call: "call" };

function Quotes({ quotes }) {
  if (!quotes?.length) return null;
  return (
    <ul className="pa-citations">
      {quotes.slice(0, 2).map((q, i) => (
        <li key={i}>
          “{q.quote}”
          <span className="pa-citation__src"> — {KIND_LABEL[q.source_kind] || "record"}{q.date ? `, ${q.date}` : ""}</span>
        </li>
      ))}
    </ul>
  );
}

export default function OutreachSnapshotCard({ definition = "quote_recovery" }) {
  const [snap, setSnap] = useState(null);
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [staged, setStaged] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());

  const toggleExclude = (quoteId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) next.delete(quoteId); else next.add(quoteId);
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await getOutreachSnapshot(definition);
      setSnap(data);
      setState("ready");
    } catch (e) {
      const code = e?.response?.status ?? e?.status; // api/client normalises it onto e.status
      setState(code === 404 ? "none" : code === 403 ? "forbidden" : "error");
    }
  }, [definition]);

  useEffect(() => { load(); }, [load]);

  if (state === "loading") return <div className="pa-card pa-card--muted">Loading outreach review…</div>;
  if (state === "none") {
    // A silent null here made "wrong tenant" indistinguishable from "broken".
    // Name the workspace so correct emptiness reads as what it is.
    return (
      <div className="pa-card pa-card--muted">
        No campaign review for {activeTenantName()} yet — if you expected one,
        check that you’re in the right workspace.
      </div>
    );
  }
  // A 403 is the server refusing this sign-in, not a blip: "try again" would be a lie.
  if (state === "forbidden") return <div className="pa-card pa-card--muted">The quote-recovery review isn’t available to this sign-in (the server refused it).</div>;
  if (state === "error") return <div className="pa-card pa-card--muted">The quote-recovery review didn’t load. Reload the page to try again.</div>;

  const c = snap.classification_counts || {};
  const stage = async () => {
    setBusy(true);
    try {
      const { data } = await approveOutreachSnapshot(snap.snapshot_id, Array.from(excluded));
      setStaged(data);
      await load();
    } catch (e) {
      setStaged({ error: e?.response?.data?.detail || "Staging failed — try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pa-card">
      <div className="pa-card__head">
        <h3>Quote Recovery — Review</h3>
        <span className="pa-count">{(snap.ranked_pool || []).length}</span>
      </div>

      <div className="pa-outcome-counters">
        <span className="pa-counter pa-counter--nonzero">Ready {(snap.ranked_pool || []).length}</span>
        <span className="pa-counter">Said yes {c.ALREADY_RESPONDED ?? 0}</span>
        <span className="pa-counter">Declined {c.ALREADY_DECLINED ?? 0}</span>
        <span className="pa-counter">No reply yet {c.CONTACTED_NO_REPLY ?? 0}</span>
        <span className="pa-counter">Never contacted {c.NEVER_CONTACTED ?? 0}</span>
        <span className="pa-counter">On hold {(snap.holds || []).length}</span>
        <span className="pa-counter">Excluded {(snap.exclusion_ledger || []).length}</span>
      </div>

      {(snap.follow_through || []).length > 0 && (
        <details className="pa-evidence" open>
          <summary>Said yes — follow through, don’t re-pitch ({snap.follow_through.length})</summary>
          <ol className="pa-targets">
            {snap.follow_through.map((t, i) => (
              <li key={i} className="pa-target">
                <strong>{t.name}</strong>
                <Quotes quotes={t.evidence} />
              </li>
            ))}
          </ol>
        </details>
      )}

      <details className="pa-evidence" open>
        <summary>Ranked outreach pool ({(snap.ranked_pool || []).length}) — thread-aware drafts</summary>
        <ol className="pa-targets">
          {(snap.ranked_pool || []).slice(0, 200).map((t) => (
            <li key={t.rank} className={`pa-target${excluded.has(String(t.quote_id)) ? " pa-target--excluded" : ""}`}>
              <div className="pa-target__head">
                <strong>{t.name}</strong>
                <span className="pa-target__risk">
                  {money(t.quote_total)} · {t.age_days}d
                  {t.stall_reason ? ` · ${t.stall_reason.replace(/_/g, " ")}` : ""}
                </span>
                {snap.status === "pending_review" && (
                  <button
                    type="button"
                    className="pa-btn pa-btn--dismiss pa-btn--tiny"
                    onClick={() => toggleExclude(String(t.quote_id))}
                  >
                    {excluded.has(String(t.quote_id)) ? "Include" : "Exclude"}
                  </button>
                )}
              </div>
              {(t.prior_touches || []).length > 0 && (
                <div className="pa-target__fallback">
                  Prior touch: {t.prior_touches[t.prior_touches.length - 1].actor}, {t.prior_touches[t.prior_touches.length - 1].date}
                </div>
              )}
              <Quotes quotes={t.evidence} />
              <div className="pa-draft">“{t.draft}”</div>
            </li>
          ))}
        </ol>
      </details>

      {(snap.holds || []).length > 0 && (
        <details className="pa-evidence">
          <summary>Hold queue — needs a human read ({snap.holds.length})</summary>
          <ol className="pa-targets">
            {snap.holds.map((t, i) => (
              <li key={i} className="pa-target">
                <strong>{t.name}</strong>
                <span className="pa-target__fallback"> {t.demoted_reason}</span>
                <Quotes quotes={t.evidence} />
              </li>
            ))}
          </ol>
        </details>
      )}

      {(snap.exclusion_ledger || []).length > 0 && (
        <details className="pa-evidence">
          <summary>Exclusion ledger ({snap.exclusion_ledger.length}) — every exclusion has a reason</summary>
          <ol className="pa-targets">
            {snap.exclusion_ledger.slice(0, 60).map((t, i) => (
              <li key={i} className="pa-target">
                <strong>{t.name}</strong>
                <span className="pa-target__fallback"> {String(t.reason || "").replace(/_/g, " ")}{t.dnc_marked ? " · DNC marked" : ""}</span>
                <Quotes quotes={t.quotes} />
              </li>
            ))}
          </ol>
        </details>
      )}

      {snap.status === "pending_review" ? (
        <div className="pa-actions">
          <button className="pa-btn pa-btn--approve" disabled={busy} onClick={stage}>
            {busy
              ? "Staging…"
              : `Stage ${(snap.ranked_pool || []).length - excluded.size} for send${excluded.size ? ` (${excluded.size} excluded)` : ""}`}
          </button>
        </div>
      ) : (
        <div className="pa-status">Reviewed {snap.status === "reviewed" ? "— staged on AI Proposals for the final Approve & send" : `(${snap.status})`}</div>
      )}
      {staged && (
        <div className="pa-note">
          {staged.error ? staged.error : `Staged ${staged.targets} targets — final send happens on the AI Proposals card (sandboxed until the live flip).`}
        </div>
      )}
    </div>
  );
}
